/**
 * Browser-side half of the Campfire Web MCP bridge.
 *
 * A web page cannot bind a loopback MCP endpoint, so this module exposes the
 * existing AI registry/executor through a narrow same-window message channel.
 * The companion Chrome extension carries those messages to the local relay in
 * `mcp/server/index.mjs`; it never re-implements a Campfire tool.
 *
 * The bridge is enabled in Vite development builds by default. Set
 * `VITE_CAMPFIRE_MCP_ENABLED=false` to turn it off in development, or `true`
 * to opt a non-development build in explicitly.
 */

import { Ioc } from "@core/ioc/ioc";
import {
  AI_AGENT_TOOLS_MODEL,
  TOOLS_DISABLED_FOR_AGENTS,
  type AIAgentTool,
  type IAIAgentToolsModel,
} from "@ai/registry/AIAgentToolRegistryModel";
import {
  REALTIME_CONVERSATION_LOG,
  type IRealtimeConversationLog,
  type RealtimeLogEntry,
} from "@ai/observability/RealtimeConversationLog";

const K_EXTENSION_SOURCE = "campfire-mcp-extension";
const K_PAGE_SOURCE = "campfire-mcp-page";
const K_PROTOCOL_VERSION = 1;
const K_TRIGGERED_BY = "MCP";
const K_MAX_LOG_ENTRIES = 50;

const K_STATUS_TOOL = "Get_Web_Runtime_Status";
const K_EXTRACT_LOG_TOOL = "Extract_Web_AI_Log";

type BridgeMethod = "tools/list" | "tools/call";

interface PageBridgeRequest {
  readonly source: typeof K_EXTENSION_SOURCE;
  readonly version: typeof K_PROTOCOL_VERSION;
  readonly type: "request";
  readonly requestId: string;
  readonly method: BridgeMethod;
  readonly params?: unknown;
}

interface ToolCallParams {
  readonly name: string;
  readonly arguments: Record<string, unknown>;
}

interface McpContentBlock {
  readonly type: "text";
  readonly text: string;
}

interface McpToolCallResult {
  readonly content: McpContentBlock[];
  readonly isError: boolean;
}

interface McpToolDefinition {
  readonly name: string;
  readonly title?: string;
  readonly description: string;
  readonly inputSchema: Record<string, unknown>;
  readonly annotations: {
    readonly readOnlyHint: boolean;
    readonly destructiveHint: boolean;
    readonly openWorldHint: boolean;
  };
  readonly _meta: Record<string, unknown>;
}

class CampfireMcpPageBridge {
  private readonly m_tools: IAIAgentToolsModel;
  private readonly m_log: IRealtimeConversationLog | null;
  private m_toolCallQueue: Promise<void> = Promise.resolve();

  constructor(tools: IAIAgentToolsModel, log: IRealtimeConversationLog | null) {
    this.m_tools = tools;
    this.m_log = log;
  }

  start(): void {
    window.addEventListener("message", this.onMessage);
    this.post({
      source: K_PAGE_SOURCE,
      version: K_PROTOCOL_VERSION,
      type: "ready",
      details: this.runtimeStatus(),
    });
  }

  private readonly onMessage = (event: MessageEvent<unknown>): void => {
    if (event.source !== window || event.origin !== window.location.origin) return;
    const request = event.data;
    if (!isPageBridgeRequest(request)) return;

    void this.handleRequest(request).then(
      (result) => this.postResponse(request.requestId, true, result),
      (error: unknown) => this.postResponse(
        request.requestId,
        false,
        undefined,
        error instanceof Error ? error.message : String(error),
      ),
    );
  };

  private async handleRequest(request: PageBridgeRequest): Promise<unknown> {
    switch (request.method) {
      case "tools/list":
        return { tools: this.listTools() };
      case "tools/call":
        return this.callTool(parseToolCallParams(request.params));
    }
  }

  private listTools(): McpToolDefinition[] {
    // Refresh runtime-composed schema fields such as the switch_role enum.
    // The returned filtered list is intentionally ignored: MCP exposes every
    // agent-eligible registered tool so an external agent can discover project
    // tools before it opens a project. Calls still honor each tool's live
    // enabled state and the normal writer guardrails.
    this.m_tools.getEnabledToolSchemas();

    return [
      createStatusTool(),
      createExtractLogTool(),
      ...this.m_tools.allTools
        .filter((tool) => !TOOLS_DISABLED_FOR_AGENTS.has(tool.name))
        .map(convertTool),
    ];
  }

  private callTool(params: ToolCallParams): Promise<McpToolCallResult> {
    if (params.name === K_STATUS_TOOL) {
      return Promise.resolve(textResult(JSON.stringify(this.runtimeStatus(), null, 2), false));
    }

    if (params.name === K_EXTRACT_LOG_TOOL) {
      return Promise.resolve(textResult(this.extractLog(params.arguments), false));
    }

    // Preserve tool order when an MCP client issues concurrent mutations. The
    // normal realtime path is sequential; the external bridge should not make
    // executor behavior depend on promise scheduling.
    const result = this.m_toolCallQueue.then(() => this.executeAiTool(params));
    this.m_toolCallQueue = result.then(() => undefined, () => undefined);
    return result;
  }

  private async executeAiTool(params: ToolCallParams): Promise<McpToolCallResult> {
    const tool = this.m_tools.allTools.find((candidate) => candidate.name === params.name);
    if (!tool || TOOLS_DISABLED_FOR_AGENTS.has(tool.name)) {
      return textResult(`tool_not_supported:${params.name}`, true);
    }

    if (!tool.enabled) {
      return textResult(
        `error: tool_disabled:${params.name}. The current Campfire state does not make this tool available.`,
        true,
      );
    }

    const result = await this.m_tools.toolExecutorModel.executeAsync(
      params.name,
      params.arguments,
      K_TRIGGERED_BY,
    );
    return textResult(result, isToolError(result));
  }

  private extractLog(args: Record<string, unknown>): string {
    const requestedCount = readBoundedInteger(args.maxEntries, K_MAX_LOG_ENTRIES);
    const maxEntries = Math.max(1, Math.min(requestedCount, K_MAX_LOG_ENTRIES));
    const includePayload = args.includePayload !== false;
    const entries = this.m_log?.entries.slice(-maxEntries) ?? [];

    return JSON.stringify({
      generatedUtc: new Date().toISOString(),
      available: this.m_log !== null,
      totalEntries: this.m_log?.entries.length ?? 0,
      returnedEntries: entries.length,
      includePayload,
      entries: entries.map((entry) => serializeLogEntry(entry, includePayload)),
    }, null, 2);
  }

  private runtimeStatus(): Record<string, unknown> {
    const agentTools = this.m_tools.allTools.filter(
      (tool) => !TOOLS_DISABLED_FOR_AGENTS.has(tool.name),
    );
    return {
      product: "Campfire Scenes Web",
      appVersion: __APP_VERSION__,
      commitSha: __COMMIT_SHA__,
      url: window.location.href,
      title: document.title,
      readyState: document.readyState,
      toolCount: agentTools.length,
      enabledToolCount: agentTools.filter((tool) => tool.enabled).length,
      unavailableToolNames: agentTools.filter((tool) => !tool.enabled).map((tool) => tool.name),
      aiLogEntryCount: this.m_log?.entries.length ?? 0,
    };
  }

  private postResponse(
    requestId: string,
    ok: boolean,
    result?: unknown,
    error?: string,
  ): void {
    this.post({
      source: K_PAGE_SOURCE,
      version: K_PROTOCOL_VERSION,
      type: "response",
      requestId,
      ok,
      result,
      error,
    });
  }

  private post(message: Record<string, unknown>): void {
    window.postMessage(message, window.location.origin);
  }
}

export function isCampfireMcpPageBridgeEnabled(): boolean {
  const configured = (import.meta.env as Record<string, unknown>)["VITE_CAMPFIRE_MCP_ENABLED"];
  if (typeof configured === "string" && configured.trim()) {
    const normalized = configured.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return import.meta.env.DEV;
}

export function startCampfireMcpPageBridge(): void {
  if (!isCampfireMcpPageBridgeEnabled()) return;
  if (!Ioc.isRegistered<IAIAgentToolsModel>(AI_AGENT_TOOLS_MODEL)) {
    console.warn("[MCP] AI tools are not registered; the web page bridge was not started.");
    return;
  }

  const log = Ioc.isRegistered<IRealtimeConversationLog>(REALTIME_CONVERSATION_LOG)
    ? Ioc.resolve<IRealtimeConversationLog>(REALTIME_CONVERSATION_LOG)
    : null;
  new CampfireMcpPageBridge(
    Ioc.resolve<IAIAgentToolsModel>(AI_AGENT_TOOLS_MODEL),
    log,
  ).start();
  console.info("[MCP] Campfire web page bridge ready for the Chrome extension.");
}

function convertTool(tool: AIAgentTool): McpToolDefinition {
  return {
    name: tool.name,
    ...(tool.humanDescription ? { title: tool.humanDescription } : {}),
    description: tool.description ?? "",
    inputSchema: tool.parameters ?? emptyInputSchema(),
    annotations: {
      readOnlyHint: !tool.writerOnly,
      destructiveHint: tool.writerOnly,
      openWorldHint: false,
    },
    _meta: {
      category: tool.category ?? "",
      humanDescription: tool.humanDescription ?? "",
      writerOnly: tool.writerOnly,
      currentlyEnabled: tool.enabled,
    },
  };
}

function createStatusTool(): McpToolDefinition {
  return {
    name: K_STATUS_TOOL,
    title: "Get Campfire web runtime status",
    description:
      "Testing-only MCP diagnostic. Reports the connected Campfire web build, URL, document state, and current AI-tool availability.",
    inputSchema: emptyInputSchema(),
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
    _meta: {
      category: "Diagnostics",
      humanDescription: "Inspect the connected Campfire web runtime.",
      mcpOnly: true,
    },
  };
}

function createExtractLogTool(): McpToolDefinition {
  return {
    name: K_EXTRACT_LOG_TOOL,
    title: "Extract Campfire web AI log",
    description:
      "Testing-only MCP diagnostic. Returns the newest entries from the in-memory Campfire realtime AI wire log. The log can contain prompt and project context and should remain local.",
    inputSchema: {
      type: "object",
      properties: {
        maxEntries: {
          type: "number",
          description: `Optional. Number of newest entries to return. Defaults to and is capped at ${K_MAX_LOG_ENTRIES}.`,
          default: K_MAX_LOG_ENTRIES,
        },
        includePayload: {
          type: "boolean",
          description: "Optional. Set false to return entry metadata without payload bodies.",
          default: true,
        },
      },
      required: [],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
    _meta: {
      category: "Diagnostics",
      humanDescription: "Extract the recent web AI wire log.",
      mcpOnly: true,
    },
  };
}

function emptyInputSchema(): Record<string, unknown> {
  return { type: "object", properties: {}, required: [] };
}

function textResult(text: string, isError: boolean): McpToolCallResult {
  return { content: [{ type: "text", text }], isError };
}

function isToolError(result: string): boolean {
  if (!result.trim()) return true;
  const normalized = result.trimStart().toLowerCase();
  return normalized.startsWith("error:")
    || normalized.startsWith("tool_error:")
    || normalized.startsWith("tool_not_supported:");
}

function serializeLogEntry(
  entry: RealtimeLogEntry,
  includePayload: boolean,
): Record<string, unknown> {
  return {
    id: entry.id,
    at: new Date(entry.at).toISOString(),
    direction: entry.direction,
    type: entry.type,
    ...(entry.summary ? { summary: entry.summary } : {}),
    ...(includePayload && entry.payload ? { payload: entry.payload } : {}),
  };
}

function readBoundedInteger(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return Math.floor(value);
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function parseToolCallParams(value: unknown): ToolCallParams {
  if (!isRecord(value) || typeof value.name !== "string" || !value.name.trim()) {
    throw new Error("tools/call requires params.name");
  }
  if (value.arguments !== undefined && !isRecord(value.arguments)) {
    throw new Error("tools/call params.arguments must be an object");
  }
  return {
    name: value.name,
    arguments: value.arguments ?? {},
  };
}

function isPageBridgeRequest(value: unknown): value is PageBridgeRequest {
  if (!isRecord(value)) return false;
  return value.source === K_EXTENSION_SOURCE
    && value.version === K_PROTOCOL_VERSION
    && value.type === "request"
    && typeof value.requestId === "string"
    && (value.method === "tools/list" || value.method === "tools/call");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
